/**
 * Interaction helpers: what a pointer is over, and what a key means.
 *
 * Pure functions, so the canvas component stays a thin translation from DOM
 * events to reducer actions and everything with a rule in it is tested here.
 *
 * Two ways of finding a target, used deliberately:
 *
 * - **The renderer's data attributes** for clicks: the SVG element that was
 *   hit carries `data-kind` and `data-ref`, so the target is exact.
 * - **Geometry against the scene** for ports: ports are small, so the
 *   connector tool picks the nearest port within a tolerance measured in
 *   screen pixels. That is kinder to the pointer than an exact hit, works while
 *   the pointer is captured during a drag, and needs no DOM to test.
 */
import type { Scene, ScenePort, Point } from "../scene/types";
import type { EditorAction, EditorState } from "./state";
import { ZOOM_STEP } from "./viewport";

/** `note-text`: inside a note being written, where the menu is its text controls. */
export type TargetKind = "node" | "edge" | "block" | "port" | "badge" | "note" | "note-text" | "canvas";

export interface Target {
  kind: TargetKind;
  ref: string | null;
}

/** Screen pixels within which a port counts as under the pointer. */
export const PORT_TOLERANCE = 9;

/** Screen pixels a press must travel before it becomes a drag. */
export const DRAG_THRESHOLD = 3;

/** The drawn element an event landed on, from the renderer's data attributes. */
export function targetOfElement(element: Element | null): Target {
  const hit = element?.closest?.("[data-kind]");
  const kind = hit?.getAttribute("data-kind") as TargetKind | null;
  const ref = hit?.getAttribute("data-ref") ?? null;
  if (!hit || !kind || !ref) return { kind: "canvas", ref: null };
  return { kind, ref };
}

/**
 * The element a selecting click means. A port or a badge is part of its
 * owner: clicking a valve's port with the select tool selects the valve.
 */
export function selectableOf(element: Element | null, scene: Scene): string | null {
  const target = targetOfElement(element);
  if (target.kind === "canvas" || target.ref === null) return null;
  if (target.kind === "node" || target.kind === "edge" || target.kind === "block" || target.kind === "note") {
    return target.ref;
  }
  if (target.kind === "badge") return target.ref;
  // A port: its owning node, or the collapsed block that exposes it.
  const owner = element?.closest?.('[data-kind="node"], [data-kind="block"], [data-kind="edge"]');
  return owner?.getAttribute("data-ref") ?? ownerOfPort(scene, target.ref);
}

function ownerOfPort(scene: Scene, ref: string): string | null {
  const block = scene.blocks.find((candidate) => candidate.ports.some((port) => port.ref === ref));
  if (block) return block.ref;
  const node = scene.nodes.find((candidate) => candidate.ports.some((port) => port.ref === ref));
  return node?.ref ?? null;
}

/** Every port currently drawn: visible nodes' ports and collapsed blocks' exposed ports. */
export function drawnPorts(scene: Scene): ScenePort[] {
  return [...scene.nodes.flatMap((node) => node.ports), ...scene.blocks.flatMap((block) => block.ports)];
}

/**
 * The nearest drawn port to a scene point, within `tolerance` scene units.
 * When `among` is given, only those ports are considered — the valid targets
 * while connecting, so the pointer snaps to a port the connection may use.
 */
export function portAt(scene: Scene, point: Point, tolerance: number, among?: ReadonlySet<string>): string | null {
  let best: string | null = null;
  let bestDistance = tolerance;
  for (const port of drawnPorts(scene)) {
    if (among && !among.has(port.ref)) continue;
    const distance = Math.hypot(port.position.x - point.x, port.position.y - point.y);
    if (distance <= bestDistance) {
      best = port.ref;
      bestDistance = distance;
    }
  }
  return best;
}

/** The nearest point on a polyline to `point`, and how far away it is. */
export function nearestOnPath(path: readonly Point[], point: Point): { point: Point; distance: number } {
  let best = { point: path[0] ?? point, distance: Infinity };
  for (let i = 1; i < path.length; i += 1) {
    const a = path[i - 1]!;
    const b = path[i]!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lengthSquared = dx * dx + dy * dy;
    const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared));
    const on = { x: a.x + t * dx, y: a.y + t * dy };
    const distance = Math.hypot(point.x - on.x, point.y - on.y);
    if (distance < best.distance) best = { point: on, distance };
  }
  return best;
}

/** The drawn line nearest `point` within `tolerance` scene units, and where on it. */
export function edgeAt(scene: Scene, point: Point, tolerance: number): { ref: string; point: Point } | null {
  let best: { ref: string; point: Point; distance: number } | null = null;
  for (const edge of scene.edges) {
    const nearest = nearestOnPath(edge.path, point);
    if (nearest.distance <= tolerance && (!best || nearest.distance < best.distance)) {
      best = { ref: edge.ref, point: nearest.point, distance: nearest.distance };
    }
  }
  return best && { ref: best.ref, point: best.point };
}

/**
 * The component a note would attach to: the nearest one whose symbol is within
 * reach of the note's card, measured edge to edge in scene units.
 */
export function nearestComponent(scene: Scene, ref: string, reach = 96): string | null {
  const note = scene.notes.find((candidate) => candidate.ref === ref);
  if (!note) return null;
  let best: { ref: string; distance: number } | null = null;
  for (const node of scene.nodes) {
    const dx = Math.max(note.position.x - node.centre.x, 0, node.centre.x - (note.position.x + note.size.width)) - node.size.width / 2;
    const dy = Math.max(note.position.y - node.centre.y, 0, node.centre.y - (note.position.y + note.size.height)) - node.size.height / 2;
    const distance = Math.hypot(Math.max(0, dx), Math.max(0, dy));
    if (distance <= reach && (!best || distance < best.distance)) best = { ref: node.ref, distance };
  }
  return best?.ref ?? null;
}

/** Normalised rectangle between two scene points, for box selection. */
export function boxBetween(a: Point, b: Point) {
  return { minX: Math.min(a.x, b.x), minY: Math.min(a.y, b.y), maxX: Math.max(a.x, b.x), maxY: Math.max(a.y, b.y) };
}

/**
 * Drawn elements in reading order — top to bottom, then left to right — so
 * Tab moves through a schematic the way a sighted reader scans it, not in
 * whatever order the document happens to list things.
 */
export function readingOrder(scene: Scene): string[] {
  const items: { ref: string; x: number; y: number }[] = [
    ...scene.nodes.map((node) => ({ ref: node.ref, ...node.centre })),
    ...scene.blocks.filter((block) => block.collapsed).map((block) => ({ ref: block.ref, ...block.centre })),
    ...scene.edges.map((edge) => ({ ref: edge.ref, ...edge.labelPosition })),
    ...scene.notes.map((note) => ({ ref: note.ref, ...note.position })),
  ];
  // Rows of half a typical symbol's height, so small vertical offsets on a
  // line of components do not scramble the order.
  const row = (y: number) => Math.round(y / 24);
  items.sort((a, b) => row(a.y) - row(b.y) || a.x - b.x || a.ref.localeCompare(b.ref));
  return items.map((item) => item.ref);
}

/**
 * Where a menu for `ref` opens from the keyboard: just below the element, in
 * scene units, so the menu does not cover what it is about.
 */
export function menuAnchor(scene: Scene, ref: string | null): Point | null {
  if (ref === null) return null;
  const node = scene.nodes.find((candidate) => candidate.ref === ref);
  if (node) return { x: node.centre.x, y: node.centre.y + node.size.height / 2 + 12 };
  const block = scene.blocks.find((candidate) => candidate.ref === ref);
  if (block) return { x: block.centre.x, y: block.centre.y + block.size.height / 2 + 12 };
  const edge = scene.edges.find((candidate) => candidate.ref === ref);
  if (edge) return { x: edge.labelPosition.x, y: edge.labelPosition.y + 12 };
  const note = scene.notes.find((candidate) => candidate.ref === ref);
  return note ? { x: note.position.x, y: note.position.y + note.size.height + 8 } : null;
}

// --- Keyboard -----------------------------------------------------------------

/** The parts of a keyboard event the map reads, so it can be tested without a DOM. */
export interface KeyInput {
  key: string;
  shiftKey?: boolean;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
}

/** Things a key can ask for that are not reducer actions: they need the canvas. */
export type ViewCommand =
  | { kind: "zoom"; factor: number }
  | { kind: "zoom-reset" }
  | { kind: "fit" }
  | { kind: "pan"; dx: number; dy: number }
  | { kind: "menu" }
  | { kind: "cycle-selection"; step: 1 | -1 }
  | { kind: "cycle-port"; step: 1 | -1 }
  | { kind: "cycle-segment"; step: 1 | -1 }
  | { kind: "port-enter" }
  | { kind: "inspect" }
  | { kind: "edit-label" }
  /**
   * Write a new note: at `at`, or beside `anchor` (attached to it), or beside
   * the one selected component, or in the middle of the view.
   */
  | { kind: "add-note"; at?: Point; anchor?: string };

export type KeyResult =
  | { kind: "action"; action: EditorAction }
  | { kind: "view"; command: ViewCommand }
  | { kind: "none" };

const PAN_STEP = 48;

/**
 * What a key press means on the canvas (requirement 12.7).
 *
 * Every pointer interaction has a key: tools, selection (Tab), moving
 * (arrows), connecting (P to pick ports, Enter to connect), rotating and
 * flipping, deleting, undo, pan and zoom, and the context menu. Returns
 * `none` for keys the canvas should leave to the browser — including Tab past
 * the last element, so the canvas is never a keyboard trap.
 */
export function interpretKey(input: KeyInput, state: EditorState): KeyResult {
  const mod = input.metaKey || input.ctrlKey;
  const key = input.key.length === 1 ? input.key.toLowerCase() : input.key;
  const act = (action: EditorAction): KeyResult => ({ kind: "action", action });
  const view = (command: ViewCommand): KeyResult => ({ kind: "view", command });
  const connecting = state.drag?.kind === "connect";

  if (mod) {
    if (key === "z") return act({ type: input.shiftKey ? "REDO" : "UNDO" });
    if (key === "y") return act({ type: "REDO" });
    if (key === "a") return act({ type: "SELECT_ALL" });
    if (key === "c") return state.selection.size > 0 ? act({ type: "COPY_SELECTION" }) : { kind: "none" };
    if (key === "x") return state.selection.size > 0 ? act({ type: "CUT_SELECTION" }) : { kind: "none" };
    if (key === "v") return state.clipboard ? act({ type: "PASTE" }) : { kind: "none" };
    return { kind: "none" };
  }
  if (input.altKey) return { kind: "none" };

  switch (key) {
    case "Escape":
      if (state.drag) return act({ type: "CANCEL" });
      return act({ type: "CLEAR_SELECTION" });
    case "Delete":
    case "Backspace":
      return state.selection.size > 0 ? act({ type: "DELETE_SELECTION" }) : { kind: "none" };
    case "v":
      return act({ type: "SET_TOOL", tool: "select" });
    case "c":
      return act({ type: "SET_TOOL", tool: "connect" });
    case "n":
      return act({ type: "SET_TOOL", tool: "note" });
    case "r":
      return act({ type: "ROTATE_SELECTION", quarterTurns: input.shiftKey ? -1 : 1 });
    case "h":
      return act({ type: "MIRROR_SELECTION", axis: input.shiftKey ? "vertical" : "horizontal" });
    case "l":
      // Label: move the selected components' labels round the symbol.
      return state.selection.size > 0 ? act({ type: "SET_LABEL_SIDE" }) : { kind: "none" };
    case "p":
      return view({ kind: "cycle-port", step: input.shiftKey ? -1 : 1 });
    case "j": {
      // Junction: branch the selected line, so another line can join it.
      const only = state.selection.size === 1 ? [...state.selection][0]! : null;
      const line = only && state.document.edges?.some((edge) => edge.local_id === only);
      return line ? act({ type: "BRANCH_EDGE", edge: only }) : { kind: "none" };
    }
    case "b":
      // Bends: choose a segment of the selected channel to move with the arrows.
      return view({ kind: "cycle-segment", step: input.shiftKey ? -1 : 1 });
    case "Enter":
      if (state.tool === "note" && !connecting) return view({ kind: "add-note" });
      return view(connecting || state.tool === "connect" ? { kind: "port-enter" } : { kind: "inspect" });
    case "F2":
      // Edit the label in place, as in draw.io. Renaming the identifier is the
      // menu's "Rename identifier…".
      return state.selection.size === 1 ? view({ kind: "edit-label" }) : { kind: "none" };
    case "F10":
      return input.shiftKey ? view({ kind: "menu" }) : { kind: "none" };
    case "ContextMenu":
      return view({ kind: "menu" });
    case "Tab":
      return view({ kind: "cycle-selection", step: input.shiftKey ? -1 : 1 });
    case "+":
    case "=":
      return view({ kind: "zoom", factor: ZOOM_STEP });
    case "-":
    case "_":
      return view({ kind: "zoom", factor: 1 / ZOOM_STEP });
    case "0":
      return view({ kind: "zoom-reset" });
    case "!":
    case "1":
      // Shift+1 fits, as in Figma and the mockup's menu.
      return input.shiftKey || key === "!" ? view({ kind: "fit" }) : { kind: "none" };
  }

  const arrows: Record<string, [number, number]> = {
    ArrowLeft: [-1, 0],
    ArrowRight: [1, 0],
    ArrowUp: [0, -1],
    ArrowDown: [0, 1],
  };
  const arrow = arrows[input.key];
  if (arrow) {
    const [dx, dy] = arrow;
    if (state.selection.size > 0) {
      // A nudge is one grid step, so it survives snapping; Shift for four.
      const step = state.grid.spacing * (input.shiftKey ? 4 : 1);
      return act({ type: "MOVE_SELECTION", dx: dx * step, dy: dy * step });
    }
    const step = PAN_STEP * (input.shiftKey ? 4 : 1);
    // Arrow right looks further right, so the content moves left. `+ 0`
    // normalises -0 from the zero axis.
    return view({ kind: "pan", dx: -dx * step + 0, dy: -dy * step + 0 });
  }
  return { kind: "none" };
}
